/**
 * The text editor for descriptions and notes: a format switch (Auto / Text / Markdown / BBCode), a toolbar for
 * the chosen syntax (BBCode: the tags of Mantis' BBCodePlus plugin), a preview, @mention suggestions and, for
 * notes, quick texts such as "Accepted". See markdown.tsx for how each format is shown.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { Avatar, FormattedText } from './components';
import { detectFormat, type TextFormat } from './markdown';
import { t } from './i18n';

export type { TextFormat } from './markdown';

/** Colors offered by the toolbar (the ones Mantis texts use most). */
const TEXT_COLORS = ['green', 'red', 'blue', 'purple', 'brown', 'orange', 'gray', 'black'];
const HIGHLIGHTS = ['yellow', '#c1f0d0', '#cce0ff', '#ffd5d2', '#f3e8ff', '#e9f2ff'];
const SIZES: [string, string][] = [
  ['75', '75%'],
  ['125', '125%'],
  ['150', '150%'],
  ['200', '200%'],
];
const CODE_LANGS = ['pascal', 'cpp', 'csharp', 'java', 'javascript', 'python', 'sql', 'xml', 'json', 'bash'];

/** Quick texts for notes, as Mantis BBCode (the phrases teams used most in their Mantis notes). */
export const NOTE_PRESETS: { label: string; color: string; text: string }[] = [
  { label: 'Accepted', color: 'green', text: '[color=green][b]Accepted[/b][/color]' },
  { label: 'Passed', color: 'green', text: '[color=green][b]Passed[/b][/color]' },
  { label: 'Failed', color: 'red', text: '[color=red][b]Failed[/b][/color]' },
  { label: 'New Failed', color: 'red', text: '[color=red][b]New Failed[/b][/color]' },
  { label: 'Rejected', color: 'red', text: '[color=red][b]Rejected[/b][/color]' },
];

const PREF_KEY = 'bt.editor.syntax';
/** The syntax the toolbar offers for "auto" texts without any formatting yet (the last one chosen). */
function preferredSyntax(): 'markdown' | 'bbcode' {
  try {
    return localStorage.getItem(PREF_KEY) === 'markdown' ? 'markdown' : 'bbcode';
  } catch {
    return 'bbcode';
  }
}
function rememberSyntax(f: TextFormat) {
  if (f !== 'markdown' && f !== 'bbcode') return;
  try {
    localStorage.setItem(PREF_KEY, f);
  } catch {
    /* storage unavailable */
  }
}

interface MenuItem {
  label: ReactNode;
  title?: string;
  onClick: () => void;
}

/** A toolbar button with a small menu. */
function ToolMenu({ label, title, items, swatches }: { label: ReactNode; title: string; items: MenuItem[]; swatches?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);
  return (
    <span className="md-menu-wrap" ref={ref}>
      <button type="button" className="md-tool" title={title} aria-label={title} aria-expanded={open} onClick={() => setOpen(!open)}>
        {label}
        <span className="md-caret">▾</span>
      </button>
      {open && (
        <span className={swatches ? 'md-palette' : 'md-menu'}>
          {items.map((it, i) => (
            <button
              key={i}
              type="button"
              title={it.title}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
            >
              {it.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

const FORMAT_LABELS: Record<TextFormat, string> = { auto: 'Auto', text: 'Text', markdown: 'Markdown', bbcode: 'BBCode' };

export function MentionTextarea({
  value,
  onChange,
  presets,
  format,
  onFormatChange,
  ...rest
}: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> & {
  value: string;
  onChange: (v: string) => void;
  /** show the quick-text buttons (notes) */
  presets?: boolean;
  /** how the text is written; with onFormatChange the editor shows the switch */
  format?: TextFormat;
  onFormatChange?: (f: TextFormat) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [query, setQuery] = useState<{ start: number; text: string } | null>(null);
  const [active, setActive] = useState(0);
  const [preview, setPreview] = useState(false);
  const fmt: TextFormat = format ?? 'auto';
  const detected = fmt === 'auto' ? detectFormat(value) : null;
  // the syntax the toolbar writes
  const syntax: 'markdown' | 'bbcode' | null =
    fmt === 'text' ? null : fmt === 'markdown' ? 'markdown' : fmt === 'bbcode' ? 'bbcode' : detected === 'markdown' ? 'markdown' : detected === 'bbcode' || detected === 'mixed' ? 'bbcode' : preferredSyntax();

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ id: number; username: string; realname: string; enabled: boolean }[]>('/api/users'),
    staleTime: 300_000,
    enabled: query != null,
  });
  const matches = query
    ? (users.data ?? [])
        .filter((u) => u.enabled !== false)
        .filter((u) => u.username.toLowerCase().startsWith(query.text) || u.realname.toLowerCase().split(/\s+/).some((w) => w.startsWith(query.text)))
        .slice(0, 6)
    : [];
  const detect = (text: string, caret: number) => {
    const m = /(^|[\s(\[{"'>,;:!?])@([\w.+-]*)$/.exec(text.slice(0, caret));
    setQuery(m ? { start: caret - m[2].length, text: m[2].toLowerCase() } : null);
    setActive(0);
  };
  const pick = (username: string) => {
    if (!query) return;
    const el = ref.current!;
    const caret = el.selectionStart;
    onChange(value.slice(0, query.start) + username + ' ' + value.slice(caret));
    setQuery(null);
    const pos = query.start + username.length + 1;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  // editing helpers: they replace the selection and select the part to type over
  const apply = (fn: (sel: string, before: string, after: string) => { text: string; select?: [number, number] }) => {
    const el = ref.current;
    if (!el) return;
    const [a, b] = [el.selectionStart, el.selectionEnd];
    const r = fn(value.slice(a, b), value.slice(0, a), value.slice(b));
    onChange(value.slice(0, a) + r.text + value.slice(b));
    const [s, e] = r.select ?? [r.text.length, r.text.length];
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(a + s, a + e);
    });
  };
  const wrap = (open: string, close: string, placeholder: string) =>
    apply((sel) => {
      const inner = sel || placeholder;
      return { text: open + inner + close, select: [open.length, open.length + inner.length] };
    });
  /** a block on lines of its own */
  const block = (open: string, close: string, placeholder: string) =>
    apply((sel, before, after) => {
      const pre = before && !before.endsWith('\n') ? '\n' : '';
      const post = after && !after.startsWith('\n') ? '\n' : '';
      const inner = sel || placeholder;
      return { text: pre + open + inner + close + post, select: [pre.length + open.length, pre.length + open.length + inner.length] };
    });
  const prefixLines = (p: (n: number) => string) => apply((sel) => ({ text: (sel || '').split('\n').map((l, i) => p(i) + l).join('\n') }));
  const bbList = (open: string) =>
    apply((sel, before, after) => {
      const items = sel ? sel.split('\n').filter((l) => l.trim()) : [''];
      const pre = before && !before.endsWith('\n') ? '\n' : '';
      const post = after && !after.startsWith('\n') ? '\n' : '';
      const text = `${pre}${open}\n${items.map((l) => `[*]${l}`).join('\n')}\n[/list]${post}`;
      const firstItem = pre.length + open.length + 1 + 3;
      return { text, select: sel ? undefined : [firstItem, firstItem] };
    });
  const insertLine = (snippet: string) =>
    apply((_sel, before, after) => ({
      text: (before && !before.endsWith('\n') ? '\n' : '') + snippet + (after && !after.startsWith('\n') ? '\n' : ''),
    }));

  const bbTools: ReactNode = (
    <>
      <button type="button" className="md-tool" title={t('Bold')} onClick={() => wrap('[b]', '[/b]', t('bold text'))}>
        <b>B</b>
      </button>
      <button type="button" className="md-tool" title={t('Italic')} onClick={() => wrap('[i]', '[/i]', t('italic text'))}>
        <i>I</i>
      </button>
      <button type="button" className="md-tool" title={t('Underline')} onClick={() => wrap('[u]', '[/u]', t('text'))}>
        <u>U</u>
      </button>
      <button type="button" className="md-tool" title={t('Strikethrough')} onClick={() => wrap('[s]', '[/s]', t('text'))}>
        <s>S</s>
      </button>
      <span className="md-sep" />
      <ToolMenu
        label={<span className="md-color-a">A</span>}
        title={t('Text color')}
        swatches
        items={TEXT_COLORS.map((c) => ({ label: <span className="md-swatch" style={{ background: c }} />, title: c, onClick: () => wrap(`[color=${c}]`, '[/color]', t('text')) }))}
      />
      <ToolMenu
        label={<span className="md-highlight-a">A</span>}
        title={t('Highlight')}
        swatches
        items={HIGHLIGHTS.map((c) => ({ label: <span className="md-swatch" style={{ background: c }} />, title: c, onClick: () => wrap(`[highlight=${c}]`, '[/highlight]', t('text')) }))}
      />
      <ToolMenu
        label="aA"
        title={t('Text size')}
        items={[
          ...SIZES.map(([v, l]) => ({ label: <span style={{ fontSize: `${Math.min(150, Number(v))}%` }}>{l}</span>, onClick: () => wrap(`[size=${v}]`, '[/size]', t('text')) })),
          { label: <small>{t('Small')}</small>, onClick: () => wrap('[small]', '[/small]', t('text')) },
        ]}
      />
      <ToolMenu
        label="H"
        title={t('Heading')}
        items={[1, 2, 3, 4, 5, 6].map((n) => ({ label: <span className={`bb-h bb-h${n}`}>{t('Heading {n}', { n })}</span>, onClick: () => wrap(`[h${n}]`, `[/h${n}]`, t('Heading')) }))}
      />
      <ToolMenu
        label="≡"
        title={t('Alignment')}
        items={[
          { label: t('Left'), onClick: () => wrap('[left]', '[/left]', t('text')) },
          { label: t('Center'), onClick: () => wrap('[center]', '[/center]', t('text')) },
          { label: t('Right'), onClick: () => wrap('[right]', '[/right]', t('text')) },
          { label: t('Justify'), onClick: () => wrap('[justify]', '[/justify]', t('text')) },
        ]}
      />
      <span className="md-sep" />
      <button
        type="button"
        className="md-tool"
        title={t('Link')}
        onClick={() =>
          apply((sel) => {
            const txt = sel || t('text');
            const open = '[url=https://]';
            return { text: `${open}${txt}[/url]`, select: [5, open.length - 1] };
          })
        }
      >
        🔗
      </button>
      <button type="button" className="md-tool" title={t('E-mail')} onClick={() => wrap('[email]', '[/email]', 'name@example.com')}>
        @
      </button>
      <button type="button" className="md-tool" title={t('Image')} onClick={() => wrap('[img]', '[/img]', 'https://')}>
        🖼
      </button>
      <span className="md-sep" />
      <ToolMenu
        label="</>"
        title={t('Code')}
        items={[
          { label: t('Code'), onClick: () => block('[code]\n', '\n[/code]', 'code') },
          ...CODE_LANGS.map((l) => ({ label: <code>{l}</code>, onClick: () => block(`[code=${l}]\n`, '\n[/code]', 'code') })),
        ]}
      />
      <button type="button" className="md-tool" title={t('Quote')} onClick={() => block('[quote]', '[/quote]', t('text'))}>
        ❝
      </button>
      <ToolMenu
        label="•"
        title={t('List')}
        items={[
          { label: `• ${t('List')}`, onClick: () => bbList('[list]') },
          { label: `1. ${t('Numbered list')}`, onClick: () => bbList('[list=1]') },
          { label: `a. ${t('Lettered list')}`, onClick: () => bbList('[list=a]') },
          { label: `[*] ${t('List item')}`, onClick: () => insertLine('[*]') },
        ]}
      />
      <button
        type="button"
        className="md-tool"
        title={t('Table')}
        onClick={() => block('[table]\n[tr][th]', '[/th][th]B[/th][/tr]\n[tr][td]1[/td][td]2[/td][/tr]\n[/table]', 'A')}
      >
        ▦
      </button>
      <button type="button" className="md-tool" title={t('Horizontal line')} onClick={() => insertLine('[hr]')}>
        ―
      </button>
      <ToolMenu
        label="⋯"
        title={t('More')}
        items={[
          { label: <span>x<sub>2</sub> {t('Subscript')}</span>, onClick: () => wrap('[sub]', '[/sub]', t('text')) },
          { label: <span>x<sup>2</sup> {t('Superscript')}</span>, onClick: () => wrap('[sup]', '[/sup]', t('text')) },
          { label: `▶ YouTube`, onClick: () => wrap('[youtube]', '[/youtube]', 'video id') },
        ]}
      />
    </>
  );

  const mdTools: ReactNode = (
    <>
      <button type="button" className="md-tool" title={t('Bold')} onClick={() => wrap('**', '**', t('bold text'))}>
        <b>B</b>
      </button>
      <button type="button" className="md-tool" title={t('Italic')} onClick={() => wrap('*', '*', t('italic text'))}>
        <i>I</i>
      </button>
      <button type="button" className="md-tool" title={t('Code')} onClick={() => wrap('`', '`', 'code')}>
        {'</>'}
      </button>
      <span className="md-sep" />
      <ToolMenu
        label="H"
        title={t('Heading')}
        items={[1, 2, 3].map((n) => ({ label: t('Heading {n}', { n }), onClick: () => prefixLines(() => `${'#'.repeat(n)} `) }))}
      />
      <button
        type="button"
        className="md-tool"
        title={t('Link')}
        onClick={() => apply((sel) => ({ text: `[${sel || t('text')}](https://)`, select: [(sel || t('text')).length + 3, (sel || t('text')).length + 11] }))}
      >
        🔗
      </button>
      <button type="button" className="md-tool" title={t('Image')} onClick={() => apply((sel) => ({ text: `![${sel}](https://)`, select: [sel.length + 4, sel.length + 12] }))}>
        🖼
      </button>
      <span className="md-sep" />
      <button type="button" className="md-tool" title={t('List')} onClick={() => prefixLines(() => '- ')}>
        •
      </button>
      <button type="button" className="md-tool" title={t('Numbered list')} onClick={() => prefixLines((n) => `${n + 1}. `)}>
        1.
      </button>
      <button type="button" className="md-tool" title={t('Quote')} onClick={() => prefixLines(() => '> ')}>
        ❝
      </button>
      <button type="button" className="md-tool" title={t('Code block')} onClick={() => block('```\n', '\n```', 'code')}>
        {'{ }'}
      </button>
      <button type="button" className="md-tool" title={t('Horizontal line')} onClick={() => insertLine('---')}>
        ―
      </button>
    </>
  );

  const detectedLabel = detected === 'mixed' ? 'BBCode + Markdown' : detected === 'bbcode' ? 'BBCode' : detected === 'markdown' ? 'Markdown' : t('plain text');

  return (
    <div className="mention-wrap">
      <div className="md-toolbar">
        {!preview && syntax === 'bbcode' && bbTools}
        {!preview && syntax === 'markdown' && mdTools}
        <span className="md-spacer" />
        {onFormatChange && (
          <label className="md-format" title={t('How the text is written')}>
            <select
              value={fmt}
              onChange={(e) => {
                const f = e.target.value as TextFormat;
                rememberSyntax(f);
                onFormatChange(f);
              }}
            >
              {(['auto', 'text', 'markdown', 'bbcode'] as TextFormat[]).map((f) => (
                <option key={f} value={f}>
                  {f === 'auto' ? `${t('Auto')} (${detectedLabel})` : f === 'text' ? t('Text') : FORMAT_LABELS[f]}
                </option>
              ))}
            </select>
          </label>
        )}
        <button type="button" className={`md-tab ${preview ? '' : 'on'}`} onClick={() => setPreview(false)}>
          {t('Write')}
        </button>
        <button type="button" className={`md-tab ${preview ? 'on' : ''}`} onClick={() => setPreview(true)}>
          {t('Preview')}
        </button>
      </div>
      {preview && (
        <div className="md-preview" style={{ minHeight: `${(Number(rest.rows) || 4) * 1.55}em` }}>
          {value.trim() ? <FormattedText text={value} format={fmt} /> : <span className="muted">{t('Nothing to preview.')}</span>}
        </div>
      )}
      <textarea
        {...rest}
        ref={ref}
        value={value}
        hidden={preview}
        onChange={(e) => {
          onChange(e.target.value);
          detect(e.target.value, e.target.selectionStart);
        }}
        onKeyDown={(e) => {
          if (syntax && (e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'i' || e.key === 'u')) {
            e.preventDefault();
            if (syntax === 'bbcode') wrap(`[${e.key}]`, `[/${e.key}]`, t('text'));
            else if (e.key === 'b') wrap('**', '**', t('bold text'));
            else if (e.key === 'i') wrap('*', '*', t('italic text'));
            return;
          }
          if (matches.length) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : matches.length - 1)) % matches.length);
              return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault();
              pick(matches[active].username);
              return;
            }
            if (e.key === 'Escape') {
              setQuery(null);
              return;
            }
          }
          rest.onKeyDown?.(e);
        }}
        onBlur={(e) => {
          setTimeout(() => setQuery(null), 150);
          rest.onBlur?.(e);
        }}
      />
      {presets && !preview && syntax === 'bbcode' && (
        <div className="note-presets">
          {NOTE_PRESETS.map((p) => (
            <button key={p.label} type="button" className="note-preset" style={{ color: p.color }} title={p.text} onClick={() => insertLine(p.text)}>
              {p.label}
            </button>
          ))}
        </div>
      )}
      {matches.length > 0 && (
        <div className="mention-list" role="listbox">
          {matches.map((u, i) => (
            <button
              type="button"
              key={u.id}
              className={i === active ? 'active' : ''}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(u.username);
              }}
            >
              <Avatar name={u.realname || u.username} size={20} /> <strong>{u.username}</strong>
              {u.realname && <span className="muted"> {u.realname}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
